import { StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Grabber } from '../components/Grabber'
import { TopBar } from '../components/TopBar'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

export interface TransactionBreakdownScreenProps {
  /** What the recipient should expect, e.g. `$99.94`. */
  usdLabel: string
  /** The floor the trade won't execute below, e.g. `$99.81`. */
  floorLabel: string
}

/**
 * What the quoted amount means, in prose: it is an estimate, it has a floor, and
 * it settles in seconds. Opened from the ⓘ beside the amount.
 */
export function TransactionBreakdownScreen({
  usdLabel,
  floorLabel,
}: TransactionBreakdownScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const insets = useSafeAreaInsets()

  return (
    <Box>
      <Grabber />
      <TopBar title={t('withdrawAddress.breakdownTitle')} />

      {/* The design's own 16 below the paragraph, and then the home indicator's
          band — which the sheet sits under, so the inset is added rather than
          standing in for it. */}
      <Box
        style={[styles.body, { paddingBottom: BODY_PADDING + insets.bottom }]}
      >
        <Text
          variant="detail"
          style={{ color: theme.content.elements.secondary }}
        >
          {emphasise(
            t('withdrawAddress.breakdown', {
              amount: usdLabel,
              minimum: floorLabel,
            }),
            [usdLabel, floorLabel],
            theme.content.elements.primary,
          )}
        </Text>
      </Box>
    </Box>
  )
}

/**
 * The two figures, set in the emphasis the design gives them, without splitting
 * the sentence into fragments a translator can't reorder: the copy stays one
 * string, and the values are found in whatever place the translation puts them.
 */
function emphasise(sentence: string, values: string[], color: string) {
  const pattern = values.filter(Boolean).map(escapeForRegExp).join('|')
  if (!pattern) {
    return sentence
  }
  return sentence.split(new RegExp(`(${pattern})`)).map((part, index) =>
    values.includes(part) ? (
      // Index-keyed: a figure can legitimately appear twice, so the value is
      // not a key.
      <Text key={index} variant="detail" weight="medium" style={{ color }}>
        {part}
      </Text>
    ) : (
      part
    ),
  )
}

const escapeForRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Measured off the design: the body is inset 20 across and 16 down.
const BODY_PADDING = 16

const styles = StyleSheet.create({
  body: { paddingHorizontal: 20, paddingTop: BODY_PADDING },
})
